// Three-way match verdicts for the item×supplier match grid: the cell's match
// dot and the "dimensions" table inside a price cell's popover.
//
// Replaces the old per-item /10 score. Each of the five dimensions — brand,
// origin, state, spec, packaging — resolves to
//   green = as asked, or the request doesn't constrain it
//   amber = an accepted alternative (the item's acceptableAlternatives list)
//   red   = off-spec on a constrained dimension, or unverifiable (no value)
// and a cell summarizes to the WORST verdict across dimensions (worstLevel).
//
// A flexible dimension with a non-empty alternatives list is NOT open to any
// value: the asked value stays green, listed values read amber, anything else
// red — the list is a whitelist, mirroring the BE's alternatives.includes()
// gate. Flexible with an empty/absent list = open to any value.
//
// Which *Flexible flag feeds each dimension is the CALLER's decision and must
// mirror the old scorer: brand/origin as-is, state only when === true (pre-
// deploy items stay strict so chilled-vs-frozen keeps reading red), spec
// as-is, packaging !== false (BE default true; packaging was never scored
// before, so absent data must not paint the grid red).

import type { PackagingSpec } from 'apis/restaurant-inquiries';
import {
  isEmptySpec,
  packagingCompatible,
  packagingSatisfiesAlternative,
  packagingSpecOf,
} from 'pages/inquiry-processor/utils/packaging-spec';

export type DimLevel = 'green' | 'amber' | 'red';

// Reason codes, not display strings — the grid composes the human note (it
// needs the asked value for 'unknown'/'off-spec', which the verdict doesn't
// carry) and tests assert on stable codes instead of copy.
export type DimReason = 'not-specified' | 'open' | 'as-asked' | 'alternative' | 'unknown' | 'off-spec';

export interface DimVerdict {
  level: DimLevel;
  reason: DimReason;
}

const norm = (v?: string | null): string => (v ?? '').trim();

export function attrVerdict(
  want: string | null | undefined,
  got: string | null | undefined,
  flexible: boolean | undefined,
  alternatives?: string[]
): DimVerdict {
  const w = norm(want);
  if (!w) return { level: 'green', reason: 'not-specified' };
  const alts = (alternatives ?? []).map((a) => norm(a).toLowerCase()).filter(Boolean);
  if (flexible && !alts.length) return { level: 'green', reason: 'open' };
  const g = norm(got);
  // A constrained dimension can't be satisfied by an offer with no value —
  // same rule as the old scorer (request 'chilled' vs null state → red).
  if (!g) return { level: 'red', reason: 'unknown' };
  if (g.toLowerCase() === w.toLowerCase()) return { level: 'green', reason: 'as-asked' };
  if (flexible && alts.includes(g.toLowerCase())) return { level: 'amber', reason: 'alternative' };
  return { level: 'red', reason: 'off-spec' };
}

export function packagingVerdict(
  want: PackagingSpec | null | undefined,
  got: PackagingSpec | null | undefined,
  flexible: boolean,
  alternatives?: PackagingSpec[]
): DimVerdict {
  const w = packagingSpecOf(want);
  if (isEmptySpec(w)) return { level: 'green', reason: 'not-specified' };
  const alts = (alternatives ?? []).filter((a) => !isEmptySpec(packagingSpecOf(a)));
  if (flexible && !alts.length) return { level: 'green', reason: 'open' };
  const g = packagingSpecOf(got);
  // Unknown-tolerant on BOTH sides (the BE's packagingCompatible): an offer
  // with no packaging data cannot contradict the ask, so it reads green here
  // rather than red like the string attributes — packaging data is the
  // sparsest field and a hard-red default would flood the grid.
  if (packagingCompatible(w, g)) return { level: 'green', reason: 'as-asked' };
  if (flexible && alts.some((a) => packagingSatisfiesAlternative(a, g)))
    return { level: 'amber', reason: 'alternative' };
  return { level: 'red', reason: 'off-spec' };
}

// Commercial verdict of a price against the item's ask: green beats the
// target, amber beats what the restaurant pays today, red beats neither.
// 'none' = nothing to compare against (neither target nor current price).
export type PriceBand = 'green' | 'amber' | 'red' | 'none';

export function priceBand(
  target: number | null | undefined,
  current: number | null | undefined,
  price: number | null | undefined
): PriceBand {
  if (price == null) return 'none';
  if (target != null && price <= target) return 'green';
  if (current != null && price <= current) return 'amber';
  return target != null || current != null ? 'red' : 'none';
}

export function worstLevel(verdicts: Array<Pick<DimVerdict, 'level'>>): DimLevel {
  if (verdicts.some((v) => v.level === 'red')) return 'red';
  if (verdicts.some((v) => v.level === 'amber')) return 'amber';
  return 'green';
}

// The most optimistic of several per-quote levels — a multi-offer cell face
// summarizes what the operator would AWARD (the best offer), so its dot leans
// best while each quote keeps its own worstLevel verdict in the popover.
export function bestLevel(levels: DimLevel[]): DimLevel {
  if (levels.some((l) => l === 'green')) return 'green';
  if (levels.some((l) => l === 'amber')) return 'amber';
  return levels.length ? 'red' : 'green';
}
